// © Microsoft Corporation. All rights reserved.

import { IClientSettings } from "./ClientSettings";
import { tryParseJson } from "../utilities/Parsing";
import { getLocalStorage } from "../webstorage";

/**
 * The response from the HeadData API
 */
export interface HeadDataResponse {
    /** The client settings object. */
    clientSettings: IClientSettings;

    /** The info string, containing flights. */
    info: string;
}

/**
 * Gets the local storage key for the head data.
 * @param pagetype The page type of the page used as local storage cache key
 */
export function getStoredHeadDataKey(pagetype: string): string {
    return "shd_" + pagetype;
}

/**
 * Gets the stored head data, or null if none is stored.
 * @param pagetype The page type of the page used as local storage cache key
 * @return The stored head data if found, otherwise return null
 */
export function getStoredHeadData(pagetype: string): HeadDataResponse {
    const storageKey: string = getStoredHeadDataKey(pagetype);
    const storedHeadDataJson: string = getLocalStorage().getItem(storageKey);
    return (storedHeadDataJson) ? tryParseJson(storedHeadDataJson) : null;
}

/**
 * Sets the stored head data. Pass in null to clear the stored data.
 * @param pagetype The page type of the page used as local storage cache key
 * @param headDataResponse The head data response from api call to be set to local storage
 */
export function setStoredHeadData(pagetype: string, headDataResponse: HeadDataResponse) {
    const storageKey: string = getStoredHeadDataKey(pagetype);

    if (headDataResponse) {
        getLocalStorage().setItem(storageKey, JSON.stringify(headDataResponse));
    } else {
        getLocalStorage().removeItem(storageKey);
    }
}